import { useEffect, useState } from "react";
import { X, Download, ExternalLink, Eye } from "lucide-react";

/**
 * PdfPreviewModal — opens a PDF from a URL (with auth bearer) inside an iframe.
 *
 * Props:
 * - url: API path (will fetch via axios with auth bearer to support cookies)
 * - filename: download filename
 * - title: modal title
 * - onClose: close handler
 * - api: axios instance (optional — pass api from /lib/api so it gets bearer)
 */
export default function PdfPreviewModal({ url, filename, title = "PDF-Vorschau", onClose, api }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    let activeBlob = null;
    (async () => {
      try {
        if (!api) throw new Error("api instance fehlt");
        const res = await api.get(url, { responseType: "blob" });
        if (!alive) return;
        activeBlob = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setBlobUrl(activeBlob);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.data?.detail || e.message || "Fehler beim Laden");
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (activeBlob) URL.revokeObjectURL(activeBlob);
    };
  }, [url, api]);

  function download() {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "dokument.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  function openNewTab() {
    if (!blobUrl) return;
    window.open(blobUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="pdf-preview-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-md w-full max-w-5xl h-[90vh] flex flex-col">
        <div className="p-4 border-b border-zinc-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Eye className="w-4 h-4 text-blue-400 shrink-0"/>
            <div className="truncate">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Vorschau</div>
              <div className="font-display font-semibold text-zinc-50 truncate">{title}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={openNewTab} disabled={!blobUrl}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 text-zinc-100 rounded"
              data-testid="pdf-preview-newtab">
              <ExternalLink className="w-3.5 h-3.5"/> Neuer Tab
            </button>
            <button onClick={download} disabled={!blobUrl}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded font-medium"
              data-testid="pdf-preview-download">
              <Download className="w-3.5 h-3.5"/> Herunterladen
            </button>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100 p-1" data-testid="pdf-preview-close">
              <X className="w-5 h-5"/>
            </button>
          </div>
        </div>
        <div className="flex-1 bg-zinc-950 overflow-hidden">
          {loading && (
            <div className="h-full flex flex-col items-center justify-center text-zinc-500 text-sm">
              <div className="w-7 h-7 border-2 border-zinc-700 border-t-blue-500 rounded-full animate-spin mb-3"></div>
              PDF wird geladen…
            </div>
          )}
          {error && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-red-400 text-sm p-6 text-center">
              <div className="font-medium mb-1">PDF konnte nicht geladen werden</div>
              <div className="text-xs text-red-300/70">{error}</div>
            </div>
          )}
          {blobUrl && !error && (
            <iframe
              src={blobUrl}
              title="PDF Vorschau"
              className="w-full h-full"
              data-testid="pdf-preview-iframe"
            />
          )}
        </div>
      </div>
    </div>
  );
}
